import { WORDMARK, MARK, srcSet } from "@/assets/brand";

type Props = {
  size?: number;
  variant?: "mark" | "wordmark" | "lockup";
  className?: string;
  /** Priority (above-the-fold) — eager + high fetch priority. Defaults to true. */
  priority?: boolean;
  /** `sizes` attribute for responsive delivery. */
  sizes?: string;
};

// Intrinsic pixel dimensions — for width/height (reserves layout space, no CLS).
const WORDMARK_INTRINSIC = WORDMARK.intrinsic;
const MARK_INTRINSIC = MARK.intrinsic;

/**
 * BrandMark — Optimize Plus logo.
 *
 * Responsive delivery via <picture>: AVIF (best compression) → WebP →
 * PNG fallback. Multiple width variants let the browser pick the smallest
 * bitmap for the viewport × DPR. Width/height attributes preserve intrinsic
 * aspect ratio so no layout shift occurs before the image decodes. Nav/footer
 * brand chrome always signals low fetchPriority so the LCP hero on the
 * homepage wins the initial mobile bandwidth window.
 */
export function BrandMark({
  size = 36,
  variant = "lockup",
  className,
  priority = true,
  sizes,
}: Props) {
  const loading = priority ? "eager" : "lazy";
  // Brand chrome must NEVER outrank the LCP hero for bandwidth on mobile.
  const fetchPriority = "low" as const;

  if (variant === "wordmark") {
    const displayHeight = size;
    const displayWidth = Math.round(size * (WORDMARK_INTRINSIC.w / WORDMARK_INTRINSIC.h));
    const sizesAttr = sizes ?? `${displayWidth}px`;
    return (
      <picture>
        <source type="image/avif" srcSet={srcSet(WORDMARK.avif)} sizes={sizesAttr} />
        <source type="image/webp" srcSet={srcSet(WORDMARK.webp)} sizes={sizesAttr} />
        <img
          src={WORDMARK.png[1].url}
          srcSet={srcSet(WORDMARK.png)}
          sizes={sizesAttr}
          alt="Optimize Plus — Austin SEO and digital marketing agency"
          width={displayWidth}
          height={displayHeight}
          style={{ height: displayHeight, width: "auto" }}
          className={className}
          loading={loading}
          decoding="async"
          fetchPriority={fetchPriority}
        />
      </picture>
    );
  }

  if (variant === "mark") {
    const sizesAttr = sizes ?? `${size}px`;
    return (
      <picture>
        <source type="image/avif" srcSet={srcSet(MARK.avif)} sizes={sizesAttr} />
        <source type="image/webp" srcSet={srcSet(MARK.webp)} sizes={sizesAttr} />
        <img
          src={MARK.png[1].url}
          srcSet={srcSet(MARK.png)}
          sizes={sizesAttr}
          alt="Optimize Plus logo"
          width={size}
          height={size}
          className={className}
          loading={loading}
          decoding="async"
          fetchPriority={fetchPriority}
        />
      </picture>
    );
  }

  const sizesAttr = sizes ?? `${size}px`;
  return (
    <span className={`inline-flex items-center gap-2.5 ${className ?? ""}`}>
      <picture>
        <source type="image/avif" srcSet={srcSet(MARK.avif)} sizes={sizesAttr} />
        <source type="image/webp" srcSet={srcSet(MARK.webp)} sizes={sizesAttr} />
        <img
          src={MARK.png[1].url}
          srcSet={srcSet(MARK.png)}
          sizes={sizesAttr}
          alt=""
          width={size}
          height={size}
          loading={loading}
          decoding="async"
          fetchPriority={fetchPriority}
        />
      </picture>
      <span className="font-display text-[1.1rem] leading-none tracking-tight">
        Optimize<span className="text-primary font-semibold">+</span>
      </span>
    </span>
  );
}
